'use client';

import { ReactNode, useEffect, useState } from 'react';
import { ArrowUpRightIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';
import { transformCloudinaryUrl } from 'shared/types/utils/cloudinary';
import { creditHost, UGC_PHOTO_POLICY_URL, type UgcPhotoCard } from 'helpers/ugc';
import BrandImage from './brand-image';
import CtaChevron from './cta-chevron';
import { externalLinkProps } from './external-link';

// Shared by the UGC preview section and the /customer-projects page: justified photo rows and the
// full-size lightbox.

// Panoramas and very tall portraits are cropped into this band so no tile becomes a sliver.
const MIN_RATIO = 0.67;
const MAX_RATIO = 1.6;
const GAP = 'gap-8';

// Lightbox delivery. The viewer shows one photo at a time, so instead of a srcset (where the browser's
// pick can't be predicted, and a preload may fetch a different file than the one shown) each photo
// gets one URL sized to what the viewer will actually display: the frame is 85vw × 78vh, the photo
// is contained in it, times the screen's pixel density (capped at 2×), rounded up to a fixed rung so
// the CDN cache stays warm, and never past the original's width.
const LIGHTBOX_RUNGS = [640, 800, 1000, 1200, 1600, 2000];
const LIGHTBOX_FRAME = { vw: 0.85, vh: 0.78 };

const lightboxSrc = (photo: UgcPhotoCard) => {
  const cssWidth = Math.min(window.innerWidth * LIGHTBOX_FRAME.vw, window.innerHeight * LIGHTBOX_FRAME.vh * (photo.width / photo.height));
  const needed = cssWidth * Math.min(window.devicePixelRatio || 1, 2);
  const rung = LIGHTBOX_RUNGS.find((w) => w >= needed) ?? LIGHTBOX_RUNGS[LIGHTBOX_RUNGS.length - 1];
  return transformCloudinaryUrl(photo.src, { width: Math.min(rung, photo.width) }) ?? photo.src;
};

// Warms the browser cache for a photo's lightbox file. Called on tile hover/press (so the first
// open is usually ready by the time the click lands) and for the neighbours of the open photo.
// Each URL is requested once per page view.
const preloaded = new Set<string>();
const preloadLightbox = (photo: UgcPhotoCard | undefined) => {
  if (!photo) return;
  const src = lightboxSrc(photo);
  if (preloaded.has(src)) return;
  preloaded.add(src);
  new Image().src = src;
};

type Tile = { photo: UgcPhotoCard; index: number; ratio: number };
export type Row = Tile[];

/**
 * Justified rows: each row is packed until its photos' combined aspect ratio (its width in units of
 * its height) is nearest `target`, then stretched to the full width — so every tile in a row shares
 * one height and both side edges stay flush. A trailing row too short to fill is dropped, unless
 * `keepTrailing` (the end of the full list), when it's absorbed or stretched so the bottom stays square.
 */
export const packRows = (photos: UgcPhotoCard[], target: number, { maxRows = Infinity, keepTrailing = false } = {}): Row[] => {
  const rows: Row[] = [];
  let tiles: Tile[] = [];
  let sum = 0;
  for (const [index, photo] of photos.entries()) {
    const ratio = Math.min(MAX_RATIO, Math.max(MIN_RATIO, photo.width / photo.height));
    // Close the row before this photo when that lands nearer the target than adding it would, so
    // row heights stay close to one another instead of drifting with each overshoot.
    if (tiles.length && Math.abs(sum - target) < Math.abs(sum + ratio - target)) {
      rows.push(tiles);
      if (rows.length === maxRows) return rows;
      tiles = [];
      sum = 0;
    }
    tiles.push({ photo, index, ratio });
    sum += ratio;
  }
  if (!tiles.length || rows.length === maxRows) return rows;
  if (sum >= target * 0.85) {
    rows.push(tiles);
  } else if (keepTrailing) {
    // End of the whole list: never leave a ragged last row. A small remainder joins the row above
    // (which gets a little shorter); a larger one, or the only row, is stretched to full width.
    const last = rows.at(-1);
    if (last && sum < target * 0.5) last.push(...tiles);
    else rows.push(tiles);
  }
  return rows;
};

/** Desktop and mobile pack to different row widths, so the layout needs to know which applies. */
export const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(true);
  useEffect(() => {
    const query = window.matchMedia('(min-width: 768px)');
    const update = () => setIsDesktop(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return isDesktop;
};

interface UgcRowsProps {
  rows: Row[];
  /** Width of the photo area as a share of the viewport on desktop, for each tile's `sizes`. */
  areaVw: number;
  onOpen: (index: number) => void;
}

export const UgcRows = ({ rows, areaVw, onOpen }: UgcRowsProps) => (
  <div className={`flex flex-col ${GAP}`}>
    {rows.map((tiles) => {
      const total = tiles.reduce((sum, tile) => sum + tile.ratio, 0);
      return (
        <div key={tiles[0].photo.id} className={`flex ${GAP}`}>
          {tiles.map(({ photo, index, ratio }) => {
            const share = ratio / total;
            return (
              // flex-grow ∝ ratio with a 0 basis makes every tile in a row the same height even with
              // gaps: width_i = (W − gaps) · ratio_i / Σratio.
              <button
                key={photo.id}
                type="button"
                onClick={() => onOpen(index)}
                onPointerEnter={() => preloadLightbox(photo)}
                onFocus={() => preloadLightbox(photo)}
                aria-label={`View photo: ${photo.alt}`}
                className="group relative min-w-0 overflow-hidden bg-[#e8e8e8]"
                style={{ flex: `${ratio} 1 0%`, aspectRatio: ratio }}
              >
                <BrandImage
                  src={photo.src}
                  alt={photo.alt}
                  width={Math.round(share * 1600)}
                  aspectRatio={ratio.toFixed(2)}
                  sizes={`(min-width: 768px) ${Math.round(share * areaVw)}vw, ${Math.round(share * 100)}vw`}
                  className="absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                />
                {/* Plain text, not a link — the tile is already a button. The linked credit is in the
                    lightbox. Shown on hover/focus so the grid stays clean but the credit is findable. */}
                {photo.credit && (
                  <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-12 pb-8 pt-24 text-left font-gt text-12 text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                    Photo: {photo.credit.name}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      );
    })}
  </div>
);

/**
 * Authenticity callout. Every photo is a real install, reviewed by hand before it's published, and
 * nothing is AI-generated — the pipeline rejects AI-labelled images and submitters confirm it on upload.
 */
interface UgcAuthenticNoteProps {
  /** `card` (gallery sections), `inline` (one strong line under a page heading), `onDark` (lightbox). */
  variant?: 'card' | 'inline' | 'onDark';
  className?: string;
}

export const UgcAuthenticNote = ({ variant = 'card', className = '' }: UgcAuthenticNoteProps) => {
  const onDark = variant === 'onDark';
  const link = (
    <a href={UGC_PHOTO_POLICY_URL} {...externalLinkProps(UGC_PHOTO_POLICY_URL)} className={`inline-flex items-center font-gt-s font-bold hover:underline ${onDark ? 'text-white' : 'text-[#212121]'}`}>
      Our photo policy
      <CtaChevron />
    </a>
  );

  if (variant === 'inline') {
    return (
      <p className={`flex flex-wrap items-center gap-x-8 gap-y-4 font-gt text-14 text-[#212121] ${className}`}>
        <CheckBadgeIcon className="size-20 shrink-0 text-[#1c1c1c]" aria-hidden />
        <span className="font-medium">100% real customer photos.</span>
        <span className="text-[#5e5e5e]">Never AI-generated or AI-edited.</span>
        {link}
      </p>
    );
  }

  // In the lightbox it stays one quiet line under each photo.
  if (onDark) {
    return (
      <p className={`flex flex-wrap items-center justify-center gap-x-8 gap-y-4 font-gt text-12 text-white/80 ${className}`}>
        <CheckBadgeIcon className="size-16 shrink-0 text-white" aria-hidden />
        Real customer photos. Never AI-generated.
        {link}
      </p>
    );
  }

  return (
    <div className={`flex items-start gap-12 border-l-4 border-[#1c1c1c] bg-[#f2f2f2] p-16 ${className}`}>
      <CheckBadgeIcon className="size-28 shrink-0 text-[#1c1c1c]" aria-hidden />
      <div className="flex flex-col gap-4">
        <p className="font-gt text-16 font-medium leading-[1.3] text-[#212121]">100% real customer photos</p>
        <p className="font-gt text-14 leading-[1.5] text-[#5e5e5e]">
          Never AI-generated or AI-edited. Every photo is a real Viewrail install, reviewed by our team before it&apos;s published.
        </p>
        <p className="mt-4 text-14">{link}</p>
      </div>
    </div>
  );
};

/** Grey rows that hold a section's height until its photos arrive. */
export const UgcRowsPlaceholder = ({ count, target }: { count: number; target: number }) => (
  <div className={`flex flex-col ${GAP}`} aria-hidden>
    {Array.from({ length: count }, (_, r) => (
      <div key={r} className="w-full bg-[#e8e8e8]" style={{ aspectRatio: target }} />
    ))}
  </div>
);

interface LightboxProps {
  photos: UgcPhotoCard[];
  index: number;
  onChange: (index: number | null) => void;
  /** Shown under the caption, e.g. a link onward to the full customer gallery. */
  footer?: ReactNode;
}

// The photo's permanent code, so a customer can tell sales exactly which install they mean. Copying
// is a convenience; the code is plain selectable text either way. Rendered with key={photo.id}, so
// stepping to another photo never shows this one's "Copied".
const PhotoCode = ({ code }: { code: string }) => {
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopyStatus('copied');
    } catch {
      // Clipboard can be unavailable (permissions, insecure context); the code stays selectable.
      setCopyStatus('failed');
    }
    setTimeout(() => setCopyStatus('idle'), 1500);
  };
  const statusText = { idle: '', copied: 'Copied', failed: 'Couldn’t copy — select the code instead' }[copyStatus];
  return (
    <>
      {' · '}Photo <span className="select-all font-medium text-white/80">{code}</span>{' '}
      <button type="button" onClick={copy} className="underline hover:text-white">
        {statusText || (
          <>
            Copy<span className="sr-only"> photo code</span>
          </>
        )}
      </button>
      <span role="status" className="sr-only">
        {statusText}
      </span>
    </>
  );
};

// Full-size viewer: Escape / backdrop close, arrow keys step. Only the current photo is loaded.
export const Lightbox = ({ photos, index, onChange, footer }: LightboxProps) => {
  const photo = photos[index];
  const step = (d: number) => onChange((index + d + photos.length) % photos.length);
  // Which URL has finished loading — keyed by URL rather than a boolean, so stepping to a photo that's
  // already cached shows it at once instead of racing a reset.
  const [loadedSrc, setLoadedSrc] = useState<string>();

  // Stepping is the common next move, so fetch the photos either side of the open one.
  useEffect(() => {
    preloadLightbox(photos[(index + 1) % photos.length]);
    preloadLightbox(photos[(index - 1 + photos.length) % photos.length]);
  }, [index, photos]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onChange(null);
      if (e.key === 'ArrowRight') onChange((index + 1) % photos.length);
      if (e.key === 'ArrowLeft') onChange((index - 1 + photos.length) % photos.length);
    };
    document.addEventListener('keydown', onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [index, photos.length, onChange]);

  if (!photo) return null;
  const src = lightboxSrc(photo);
  const loaded = loadedSrc === src;
  const control = 'flex size-44 items-center justify-center text-white transition-opacity hover:opacity-70';
  return (
    <div role="dialog" aria-modal="true" aria-label={photo.alt} className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 p-24" onClick={() => onChange(null)}>
      <button type="button" aria-label="Close" onClick={() => onChange(null)} className={`${control} absolute right-16 top-16`}>
        <XMarkIcon className="size-28" />
      </button>
      <button type="button" aria-label="Previous photo" onClick={(e) => (e.stopPropagation(), step(-1))} className={`${control} absolute left-8 md:left-24`}>
        <ChevronLeftIcon className="size-32" />
      </button>
      <figure className="flex max-h-full flex-col items-center" onClick={(e) => e.stopPropagation()}>
        {/* eslint-disable-next-line @next/next/no-img-element -- one pre-sized Cloudinary URL, shared with the preload (see lightboxSrc). */}
        <img
          key={photo.id}
          src={src}
          alt={photo.alt}
          width={photo.width}
          height={photo.height}
          fetchPriority="high"
          decoding="async"
          // A cached photo can finish before React attaches onLoad; `complete` catches that case.
          ref={(img) => {
            if (img?.complete && img.naturalWidth) setLoadedSrc(src);
          }}
          onLoad={() => setLoadedSrc(src)}
          className={`max-h-[78vh] w-auto max-w-[85vw] object-contain transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
        <figcaption className="mt-16 flex flex-col items-center gap-8 font-gt text-14 text-white">
          {photo.caption && <span>{photo.caption}</span>}
          {photo.credit && (
            <span className="text-12 text-white/80">
              Photo by{' '}
              {photo.credit.url ? (
                // Outbound credit link: nofollow + ugc since the URL arrives with submitted photos.
                <a href={photo.credit.url} target="_blank" rel="ugc nofollow noopener noreferrer" className="font-medium text-white underline">
                  {photo.credit.name}
                  <span className="text-white/60">
                    {' '}
                    · {creditHost(photo.credit.url)}
                    <ArrowUpRightIcon className="mb-2 ml-2 inline-block size-12" aria-hidden />
                  </span>
                </a>
              ) : (
                <span className="font-medium text-white">{photo.credit.name}</span>
              )}
            </span>
          )}
          <span className="text-12 text-white/60">
            {index + 1} / {photos.length}
            {photo.code && <PhotoCode key={photo.id} code={photo.code} />}
          </span>
          <UgcAuthenticNote variant="onDark" />
          {footer}
        </figcaption>
      </figure>
      <button type="button" aria-label="Next photo" onClick={(e) => (e.stopPropagation(), step(1))} className={`${control} absolute right-8 md:right-24`}>
        <ChevronRightIcon className="size-32" />
      </button>
    </div>
  );
};
